<template>
  <sc-page-base
    :title="title"
    :loading="loading">
    <sc-grid v-if="dataReady"
      :data="getDataQuotations()"
      :total="quotationsCount"
      :show-total-registers="false"
      :default-filters="defaultFilters"
      :rules="rules"
      :backend-sorting="true"
      :backend-pagination="true"
      default-sort-column="create_at"
      default-sort-direction="desc"
      @submit="getQuontationsByFilter"
      @pagination="getQuotations"
      @sorting="getQuotations"
      @click="goToQuoteDetailPage">

      <template slot="filters" slot-scope="{ filters }">
        <sc-quotations-filters :filters="filters" />
      </template>

      <template slot="table-columns" slot-scope="{ props }">
        <sc-table-column
          class="quotations__col"
          field="quote_id"
          label="Id da cotação"
          width="80"
          sortable>
          {{ props.row.id }}
        </sc-table-column>
        <sc-table-column
          class="quotations__col"
          field="quote_origin_zip_code"
          label="CEP de origem"
          width="120"
          sortable>
          {{ props.row.origin_zip_code | cep }}
        </sc-table-column>
        <sc-table-column
          class="quotations__col"
          field="quote_destination_zip_code"
          label="CEP de destino"
          width="120"
          sortable>
          {{ props.row.destination_zip_code | cep }}
        </sc-table-column>
        <sc-table-column
          class="quotations__col"
          field="quote_number"
          label="Métodos retornados"
          width="120">
          {{ props.row.logistic_provider.delivery_methods.length }}
        </sc-table-column>
        <sc-table-column
          class="quotations__col"
          field="description"
          label="Métodos de envio"  
          width="120">
          <ul>
            <li
              v-for="(item, index) in props.row.logistic_provider.delivery_methods"
              :key="item.name + index">
              <sc-tooltip
                v-if="isRemovedBy(item)"
                :label="tooltipLabel(item)"
                type="is-dark"
                animated>
                <span :class="{'has-text-line-through': isRemovedBy(item)}">
                  {{ item.name }}
                </span>
              </sc-tooltip>
              <span v-else class="delivery_methods_column">{{ item.name }}</span>
            </li>
            <li
              v-for="(item, index) in (props.row.delivery_options_debug.filter((item) => !props.row.logistic_provider.delivery_methods.find((itemCustom)=> itemCustom.name == item.name)))" :key="index">
                <p class="has-text-line-through">
                  {{ item.name }}
                </p>
            </li>
          </ul>
        </sc-table-column>
        <sc-table-column
          class="quotations__col"
          field="weight"
          label="Peso"
          width="120">
          <ul>
            <li
              v-for="(volume, index) in props.row.order.volumes.slice(0, 5)"
              :key="index">
              {{ volume.weight | finance(2, ',') | weight }}
            </li>
            <li v-if="props.row.order.volumes.length >= 6">
              ... Total de volumes: {{ props.row.order.volumes.length}}
            </li>
          </ul>
        </sc-table-column>
        <sc-table-column
          class="quotations__col"
          field="dimensions_number"
          label="Dimensões"
          width="120">
          <ul>
            <li
              v-for="(item, index) in props.row.order.volumes"
              :key="index">
              {{ `L:${item.width}cm x A:${item.height}cm x P:${item.length}cm` }}
            </li>
          </ul>
        </sc-table-column>
        <sc-table-column
          class="quotations__col"
          field="final_shipping_cost"
          label="Custo de envio"
          width="120">
          <ul>
            <li
              v-for="(item, index) in props.row.logistic_provider.delivery_methods"
              :key="index">
              R$ {{ item.cost | finance(2, ',') }}
            </li>
          </ul>
        </sc-table-column>
        <sc-table-column
          class="quotations__col"
          field="freight"
          label="Frete exibido"
          width="120">
          <ul>
            <li
              v-for="(item, index) in props.row.logistic_provider.delivery_methods"
              :key="index">
              R$ {{ item.final_shipping_cost }}
            </li>
          </ul>
        </sc-table-column>
        <sc-table-column
          class="quotations__col"
          field="shipping_time"
          label="Prazo de entrega"
          width="120">
          <ul>
            <li
              v-for="(item, index) in props.row.logistic_provider.delivery_methods"
              :key="index">
              {{ formatDeliveryEstimate(item) }}
            </li>
          </ul>
        </sc-table-column>
        <sc-table-column
          class="quotations__col"
          field="create_at"
          label="Criado em"
          width="140"
          sortable>
          {{ props.row.created | date('DD/MM/YYYY HH:mm') }}
        </sc-table-column>
      </template>
    </sc-grid>
  </sc-page-base>
</template>
<script>
  import { createNamespacedHelpers } from 'vuex';
  import {
    ScButton,
    ScForm,
    ScGrid,
    ScPageBase,
    ScTableColumn,
    ScTooltip,
  } from '@intelipost-js/secure-components';
  import { Lodash, Moment } from '@intelipost-js/secure-providers';
  import ScQuotationsFilters from './components/sc-quotations-filters/ScQuotationsFilters';

  const {
    mapState,
    mapActions,
    mapGetters,
    mapMutations,
  } = createNamespacedHelpers('quote/quotations');

  export default {
    name: 'page-quotations',
    data() {
      return {
        dataReady: false,
        title: 'Lista de Cotações',
        defaultFilters: {
          created_after: this.defaultDate(),
          created_after_iso: this.defaultDateISO(),
          search_by: '',
          search_value: '',
          sort_direction: 'desc',
          sort_field: 'create_at',
          quotationId: null,
        },
        rules: {
          search_value: [
            {
              type: 'string',
              required: true,
              trigger: 'input',
              message: 'Informe um valor',
              validator: (rule, value, callback, fields) => {
                const errors = [];
                if (fields.search_by === 'quote_id' && value.length === 0) {
                  errors.push(new Error(rule.message, rule.field));
                } else if (['quote_origin_zip_code', 'quote_destination_zip_code'].indexOf(fields.search_by) > -1 && (value === '' || value.length < 8)) {
                  errors.push(new Error(rule.message, rule.field));
                }
                callback(errors);
              },
            },
          ],
        },
      };
    },
    computed: {
      ...mapState([
        'loading',
        'quotations',
        'quotation',
      ]),
      ...mapGetters([
        'quotationsFormatted',
        'quotationsCount',
      ]),
    },
    watch: {
      quotation(newValue) {
        this.quotationId = [newValue];
      },
    },
    methods: {
      ...mapActions([
        'getQuotations',
        'getQuotation',
      ]),
      ...mapMutations([
        'setLoading',
      ]),
      formatDeliveryEstimate(item) {
        const days = item.delivery_estimate_business_days;
        const minutes = item.delivery_estimate_minutes;

        if (days !== null) {
          return `${days} dias`;
        }

        return `${minutes} minutos`;
      },
      defaultDate(currentTimestamp) {
        return Moment(currentTimestamp).toDate();
      },
      getDataQuotations() {
        return (this.quotationId) ? this.quotationId : this.quotationsFormatted;
      },
      defaultDateISO(currentTimestamp) {
        return this.defaultDate(currentTimestamp).toISOString();
      },
      goToQuoteDetailPage(row = {}) {
        const { id = null } = row;

        if (id) {
          this.$router.push({
            name: 'quotation',
            params: {
              id,
            },
          });
        }
      },
      isRemovedBy(item) {
        return (item.removed_by_quote_rules || item.removed_by_return_modes);
      },
      tooltipLabel(item) {
        if (item.removed_by_quote_rules) {
          return 'Excluído por Regra de Cotação';
        }

        if (item.removed_by_return_modes) {
          return 'Excluído por Método de Retorno';
        }

        return null;
      },
      generateTimeZone() {
        const dateSplitedTimezone = new Date().toString().split(' ')[5];
        return `${dateSplitedTimezone.slice(3, 6)}:00`;
      },
      getQuontationsByFilter(params) {
        const timeZone = this.generateTimeZone();
        const {
          filters,
        } = params;

        const customParam = Lodash.cloneDeep(params);
        customParam.filters.created_after = `${params.filters.created_after.toISOString().replace('Z', '')}${timeZone}`;

        const {
          search_by,
          search_value: id,
        } = filters;
        if (search_by === 'quote_id') {
          this.getQuotation({ id, show_error: false });
        } else {
          this.getQuotations(customParam);
          this.quotationId = null;
        }
      },
    },
    created() {
      this.setLoading(true);
      this.defaultFilters.created_after = this.defaultDate(new Date().getTime());
      this.defaultFilters.created_after_iso = this.defaultDateISO(new Date().getTime());
      this.setLoading(false);
      this.dataReady = true;
    },
    components: {
      ScButton,
      ScForm,
      ScGrid,
      ScPageBase,
      ScQuotationsFilters,
      ScTableColumn,
      ScTooltip,
    },
  };
</script>

<style lang="scss" scoped>
  .quotations {
    &__col {
      cursor: pointer;
      .delivery_methods_column{
        white-space: nowrap;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// node_modules/@intelipost-modules/secure-quote/features/quotations/PageQuotations.vue